import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import { ArrowRight, Briefcase, Scale, Receipt, FileText, Sparkles, Crown } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORY_ICONS = {
  labour: Briefcase, corporate: Scale, tax: Receipt, commercial: FileText,
};

export default function Dashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [conversations, setConversations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [creating, setCreating] = useState(false);
  const [subInfo, setSubInfo] = useState(null);

  useEffect(() => {
    api.get("/conversations").then(({ data }) => setConversations(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/payments/subscription").then(({ data }) => setSubInfo(data)).catch(() => {});
  }, []);

  const startNew = async (e) => {
    e?.preventDefault();
    if (!text.trim() || creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/conversations", { title: text.slice(0, 60), language: lang });
      navigate(`/chat/${data.conversation_id}`, { state: { initialQuestion: text } });
    } finally { setCreating(false); }
  };

  const firstName = (user?.name || "").split(" ")[0] || user?.email?.split("@")[0];

  return (
    <AppShell>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <p className="text-xs tracking-[0.24em] uppercase text-[#C65D47]">{t(lang,"dash.hi")}</p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-4xl md:text-5xl mt-2" data-testid="dashboard-greeting">{firstName}</h1>
          {subInfo && (
            <Link to="/pricing" className="inline-flex items-center gap-2 border border-[#E9ECEF] bg-white px-3 py-2 hover:bg-[#F8F9FA]" data-testid="dashboard-plan-badge">
              {subInfo.subscription?.tier_id === "firm" || subInfo.subscription?.tier_id === "pro" ? <Crown className="w-4 h-4 text-[#C65D47]" /> : <Sparkles className="w-4 h-4 text-[#C65D47]" />}
              <div className="text-end">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#868E96]">{t(lang,"dash_ext.plan")}</div>
                <div className="text-sm font-medium">
                  {subInfo.subscription?.tier_name || "Free"}
                  {" "}
                  <span className="text-[#868E96] font-normal">
                    {subInfo.subscription?.question_quota === -1
                      ? `· ${t(lang,"dash_ext.unlimited")}`
                      : `· ${Math.max(0, (subInfo.subscription?.question_quota || 5) - (subInfo.usage?.questions_used || 0))} ${t(lang,"dash_ext.quota_remaining")}`}
                  </span>
                </div>
              </div>
            </Link>
          )}
        </div>
        <p className="mt-2 text-[#495057] max-w-xl">{t(lang,"common.disclaimer")}</p>

        {/* ASK BOX */}
        <section className="mt-10 bg-white border border-[#E9ECEF]">
          <div className="p-6 border-b border-[#E9ECEF] flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#C65D47]" />
            <h2 className="font-display text-2xl">{t(lang,"dash.ask_now")}</h2>
          </div>
          <form onSubmit={startNew} className="p-6">
            <textarea
              data-testid="dashboard-ask-input"
              value={text}
              onChange={(e)=>setText(e.target.value)}
              rows={3}
              placeholder={t(lang,"dash.placeholder")}
              className="w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128] text-base resize-y"
            />
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-[#868E96]">{t(lang,"common.disclaimer")}</p>
              <button
                type="submit"
                data-testid="dashboard-ask-submit"
                disabled={!text.trim() || creating}
                className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-6 py-3 hover:bg-[#1C2538] disabled:opacity-60"
              >
                {creating ? t(lang,"common.loading") : t(lang,"chat.send")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
          {/* RECENT CONVERSATIONS */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs tracking-[0.24em] uppercase font-bold text-[#868E96]">{t(lang,"dash.recent")}</h3>
              <Link to="/chat" className="text-sm underline" data-testid="dashboard-new-chat">{t(lang,"chat.new_chat")}</Link>
            </div>
            {conversations.length === 0 ? (
              <div className="bg-white border border-[#E9ECEF] p-8 text-[#868E96] text-sm" data-testid="dashboard-empty-chats">
                {t(lang,"dash.no_chats")}
              </div>
            ) : (
              <ul className="border border-[#E9ECEF] bg-white divide-y divide-[#E9ECEF]">
                {conversations.slice(0, 6).map((c) => (
                  <li key={c.conversation_id}>
                    <Link to={`/chat/${c.conversation_id}`} className="flex items-center justify-between p-4 hover:bg-[#F8F9FA] transition-colors" data-testid={`conv-link-${c.conversation_id}`}>
                      <div>
                        <div className="text-sm font-medium">{c.title}</div>
                        <div className="text-xs text-[#868E96]">{new Date(c.updated_at).toLocaleString()}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#868E96]" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* CATEGORIES */}
          <div className="lg:col-span-5">
            <h3 className="text-xs tracking-[0.24em] uppercase font-bold text-[#868E96] mb-4">{t(lang,"dash.categories")}</h3>
            <div className="grid grid-cols-2 gap-px bg-[#E9ECEF] border border-[#E9ECEF]">
              {categories.map((c) => {
                const Icon = CATEGORY_ICONS[c.key] || Scale;
                return (
                  <Link
                    key={c.key}
                    to={`/knowledge?cat=${c.key}`}
                    data-testid={`category-${c.key}`}
                    className="bg-white p-5 hover:bg-[#F8F9FA] transition-colors group"
                  >
                    <Icon className="w-5 h-5 text-[#C65D47]" />
                    <div className="mt-3 font-display text-xl">{c[lang]}</div>
                    <div className="mt-1 text-xs text-[#868E96] inline-flex items-center gap-1 group-hover:text-[#0A1128]">
                      {t(lang,"dash.explore")} <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </AppShell>
  );
}
